import { Component, OnInit } from '@angular/core';
import { ConfigCheckService } from '../../../services/config-check.service';
import {
  ConfigCheckReport,
  ConfigSettingResult,
  CrossServiceFinding,
  OverallStatus,
  PlatformConfigCheck
} from '../../../models/config-check.model';

/**
 * Deployment config check: for every API, service and app on the platform, whether the settings it
 * needs are actually present in the environment it is running in.
 *
 * Built for the moment after a deploy, when the code is out but nobody is sure the App Service
 * configuration was finished. It answers three questions a health check cannot: which settings are
 * missing or still holding a committed placeholder; where each value actually came from (an
 * application setting, or a stale file that quietly overrode it); and whether the secrets two
 * services share are the same secret, rather than merely set on both sides.
 *
 * Defaults to problems only, because on a healthy platform this page has a few hundred rows and
 * nothing to say about almost all of them.
 */
@Component({
  selector: 'app-config-check-page',
  standalone: false,
  templateUrl: './config-check-page.component.html',
  styleUrls: ['./config-check-page.component.css']
})
export class ConfigCheckPageComponent implements OnInit {
  result: PlatformConfigCheck | null = null;

  /** This portal's own build-time environment, checked in the browser and shown alongside. */
  portal: ConfigCheckReport | null = null;

  loaded = false;
  busy = false;
  error = '';

  /** Problems only by default; the toggle shows every declared setting. */
  problemsOnly = true;

  /** Free-text filter over key, service and description. */
  search = '';

  private expanded = new Set<string>();

  constructor(private configCheck: ConfigCheckService) { }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.busy = true;
    this.error = '';

    // Checked locally either way: when the account API is unreachable, this portal's own
    // configuration is often the reason, and it is the one report still worth showing.
    this.portal = this.configCheck.checkAdminPortal();

    this.configCheck.load().subscribe({
      next: result => {
        this.result = result;
        this.loaded = true;
        this.busy = false;
        this.expandProblems();
      },
      error: () => {
        this.error = 'Could not run the config check. The account API may be unreachable, '
          + 'or this account may not have SuperAdmin.';
        this.loaded = true;
        this.busy = false;
      }
    });
  }

  /** Every service report, this portal first — it is the one the reader is looking at. */
  get services(): ConfigCheckReport[] {
    const remote = this.result?.services ?? [];
    return this.portal ? [this.portal, ...remote] : remote;
  }

  get findings(): CrossServiceFinding[] {
    return this.result?.findings ?? [];
  }

  /** Cross-service findings worth acting on, which is what the summary counts. */
  get openFindings(): CrossServiceFinding[] {
    return this.findings.filter(f => f.status !== 'Ok');
  }

  /**
   * Overall verdict including this portal's own report, which the server never sees. Without
   * folding it in, a portal pointed at the wrong environment could sit under a green banner.
   */
  get status(): OverallStatus {
    const statuses = this.services.map(s => s.status);
    if (this.result) { statuses.push(this.result.status); }
    if (statuses.includes('Error')) { return 'Error'; }
    return statuses.includes('Warning') ? 'Warning' : 'Ok';
  }

  get problemCount(): number {
    return this.services.reduce((total, svc) => total + this.problems(svc).length, 0)
      + this.openFindings.length;
  }

  get settingsChecked(): number {
    return this.services.reduce((total, svc) => total + svc.settings.length, 0);
  }

  /** Settings on one service that are not Ok. */
  problems(service: ConfigCheckReport): ConfigSettingResult[] {
    return service.settings.filter(s => s.status !== 'Ok');
  }

  /** Rows to render for a service, after the problems-only toggle and the search box. */
  visibleSettings(service: ConfigCheckReport): ConfigSettingResult[] {
    const term = this.search.trim().toLowerCase();
    return service.settings.filter(setting => {
      if (this.problemsOnly && setting.status === 'Ok') { return false; }
      if (!term) { return true; }
      return setting.key.toLowerCase().includes(term)
        || setting.description.toLowerCase().includes(term)
        || service.service.toLowerCase().includes(term);
    });
  }

  /**
   * A service is hidden only when the filters leave it with nothing AND it has no problem of its
   * own to report. An unreachable service has no settings at all, so it would otherwise disappear
   * from exactly the page meant to surface it.
   */
  isHidden(service: ConfigCheckReport): boolean {
    if (!service.reachable || service.error) { return false; }
    return this.visibleSettings(service).length === 0;
  }

  get hiddenCount(): number {
    return this.services.filter(s => this.isHidden(s)).length;
  }

  toggle(service: string): void {
    if (this.expanded.has(service)) {
      this.expanded.delete(service);
    } else {
      this.expanded.add(service);
    }
  }

  isExpanded(service: string): boolean {
    return this.expanded.has(service);
  }

  /** Open the services that have something wrong, so the page lands on the answer. */
  private expandProblems(): void {
    this.expanded.clear();
    for (const service of this.services) {
      if (service.status !== 'Ok' || !service.reachable) {
        this.expanded.add(service.service);
      }
    }
  }

  /** The App Service application-setting spelling of a key — what you actually type into Azure. */
  appSettingName(key: string): string {
    return key.replace(/:/g, '__');
  }

  /**
   * Copy the missing keys for one service as application-setting names, one per line. Retyping
   * a dozen double-underscore names by hand is how a second deploy goes wrong the same way.
   */
  copyMissing(service: ConfigCheckReport): void {
    const keys = this.problems(service)
      .filter(s => s.status === 'Missing' || s.status === 'Placeholder')
      .map(s => this.appSettingName(s.key));

    if (keys.length === 0) { return; }
    void navigator.clipboard?.writeText(keys.join('\n'));
  }

  statusClass(status: string): string {
    switch (status) {
      case 'Ok': return 'st-ok';
      case 'Warning': return 'st-warn';
      case 'Missing':
      case 'Placeholder':
      case 'Invalid':
      case 'Error': return 'st-err';
      default: return '';
    }
  }

  /** How a secret is described when its value is deliberately withheld. */
  secretSummary(setting: ConfigSettingResult): string {
    if (setting.status === 'Missing') { return 'not set'; }
    const length = setting.length ? `${setting.length} chars` : 'set';
    return setting.fingerprint ? `${length} · fingerprint ${setting.fingerprint}` : length;
  }
}
